/**
 * Cliente HTTP para comunicación con la API
 * Maneja automáticamente tokens de autenticación y errores
 * Sprint 3: Incluye headers de verificación de dispositivo
 */

import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config';
import type { ApiError } from '../types';
import { getDeviceIdentity, getCurrentTimestamp } from './deviceIdentity';
import { generateRequestSignature } from '../utils/crypto';

const SERVER_UNREACHABLE_ERROR: ApiError = {
    message: 'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.',
};

function normalizeRequestError(error: unknown): unknown {
    // fetch lanza TypeError cuando la API no está disponible o la petición es bloqueada.
    // No exponemos el mensaje técnico "Failed to fetch" en la interfaz.
    if (error instanceof TypeError) {
        return SERVER_UNREACHABLE_ERROR;
    }

    return error;
}

// Rutas que requieren verificación de dispositivo
const DEVICE_VERIFIED_ROUTES = [
    '/api/location',
    '/api/push/devices/register',
    '/api/chat',
    '/api/profile',
    '/api/operator',
    '/api/driver',
    '/api/providers',
    '/api/marketplace/operator',
    '/api/marketplace/return-intents',
    '/api/maps',
    '/api/address-catalog',
    '/api/requests',
    '/api/cargo-insurance',
    '/api/quotes',
    '/api/contracts',
    '/api/carta-porte',
    '/api/users',
    '/api/ratings',
];

const ACTIVE_PROVIDER_STORAGE_KEY = 'marketplace_active_transport_provider_id';

/**
 * Verifica si una ruta requiere headers de dispositivo
 */
function requiresDeviceVerification(endpoint: string): boolean {
    return DEVICE_VERIFIED_ROUTES.some(route => endpoint.startsWith(route));
}

class ApiClient {
    private baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    private getToken(): string | null {
        return localStorage.getItem(TOKEN_STORAGE_KEY);
    }

    private getHeaders(
        includeAuth: boolean = true,
        body: BodyInit | null | undefined = undefined,
        accept: string = 'application/json'
    ): HeadersInit {
        const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
        const headers: HeadersInit = {
            'Accept': accept,
        };

        if (!isFormData && accept === 'application/json') {
            headers['Content-Type'] = 'application/json';
        }

        if (includeAuth) {
            const token = this.getToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const activeProviderId = this.getActiveProviderId();
            if (activeProviderId !== null) {
                headers['X-Transport-Provider-ID'] = String(activeProviderId);
            }
        }

        return headers;
    }

    private getActiveProviderId(): number | null {
        const rawValue = localStorage.getItem(ACTIVE_PROVIDER_STORAGE_KEY);
        const providerId = rawValue ? Number(rawValue) : 0;

        return Number.isInteger(providerId) && providerId > 0 ? providerId : null;
    }

    private prepareBody(body: unknown): BodyInit | undefined {
        if (body === undefined || body === null) {
            return undefined;
        }

        if (
            typeof body === 'string'
            || body instanceof FormData
            || body instanceof URLSearchParams
            || body instanceof Blob
        ) {
            return body;
        }

        return JSON.stringify(body);
    }

    /**
     * Normaliza el endpoint para firmar igual que Laravel Request::path()
     * Ejemplo: "/api/chat/conversations?page=1" -> "api/chat/conversations"
     */
    private normalizePathForSignature(endpoint: string): string {
        const withoutHash = endpoint.split('#')[0] ?? endpoint;
        const withoutQuery = withoutHash.split('?')[0] ?? withoutHash;
        const trimmed = withoutQuery.trim();

        if (trimmed === '') {
            return '';
        }

        if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
            try {
                const pathname = new URL(trimmed).pathname;
                return pathname.startsWith('/') ? pathname.slice(1) : pathname;
            } catch {
                return trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
            }
        }

        return trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
    }

    /**
     * Obtiene headers de dispositivo con firma HMAC si la ruta lo requiere
     * HU-FE-05: Adjuntar device headers en requests autenticados
     */
    private async getDeviceHeaders(
        method: string,
        endpoint: string
    ): Promise<Record<string, string>> {
        if (!requiresDeviceVerification(endpoint)) {
            return {};
        }

        try {
            const { uuid, secret } = getDeviceIdentity();
            const timestamp = getCurrentTimestamp();

            const path = this.normalizePathForSignature(endpoint);

            // Generar firma HMAC: METHOD.path.timestamp
            const signature = await generateRequestSignature(secret, method, path, timestamp);

            return {
                'X-Device-UUID': uuid,
                'X-Device-Timestamp': timestamp.toString(),
                'X-Device-Secret': secret,
                'X-Device-Signature': signature,
            };
        } catch (error) {
            console.warn('⚠️ Could not generate device headers:', error);
            return {};
        }
    }

    async request<T>(
        endpoint: string,
        options: RequestInit = {},
        includeAuth: boolean = true,
        suppressErrorLog: boolean = false,
    ): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`;
        const method = (options.method || 'GET').toUpperCase();
        const requestBody = options.body;

        try {
            // Obtener headers de dispositivo con firma si aplica
            const deviceHeaders = await this.getDeviceHeaders(method, endpoint);

            const response = await fetch(url, {
                ...options,
                headers: {
                    ...this.getHeaders(includeAuth, requestBody),
                    ...deviceHeaders,
                    ...options.headers,
                },
                body: requestBody,
            });

            return await this.handleResponse<T>(response);
        } catch (error) {
            if (!suppressErrorLog) {
                console.error('API Error:', error instanceof Error ? error.message : String(error));
            }
            throw normalizeRequestError(error);
        }
    }

    async requestBlob(
        endpoint: string,
        options: RequestInit = {},
        includeAuth: boolean = true
    ): Promise<{ blob: Blob; headers: Headers }> {
        const url = `${this.baseUrl}${endpoint}`;
        const method = (options.method || 'GET').toUpperCase();
        const requestBody = options.body;

        try {
            const deviceHeaders = await this.getDeviceHeaders(method, endpoint);

            const response = await fetch(url, {
                ...options,
                headers: {
                    ...this.getHeaders(includeAuth, requestBody, '*/*'),
                    ...deviceHeaders,
                    ...options.headers,
                },
                body: requestBody,
            });

            if (!response.ok) {
                throw await this.handleErrorResponse(response);
            }

            return {
                blob: await response.blob(),
                headers: response.headers,
            };
        } catch (error) {
            console.error('API Blob Error:', error instanceof Error ? error.message : String(error));
            throw normalizeRequestError(error);
        }
    }

    private async handleResponse<T>(response: Response): Promise<T> {
        // Las respuestas 204/205 no incluyen cuerpo. Leer el texto primero también
        // cubre respuestas exitosas vacías sin intentar convertirlas a JSON.
        const responseText = await response.text();
        const hasResponseBody = responseText.trim() !== '';
        let data: T | ApiError | null = null;

        if (hasResponseBody) {
            try {
                data = JSON.parse(responseText) as T | ApiError;
            } catch (parseError) {
                console.error('❌ JSON Parse Error:', parseError);
                throw new Error(`Error parseando respuesta del servidor (status: ${response.status})`);
            }
        }

        // Si la respuesta no es exitosa, lanzar error
        if (!response.ok) {
            const error = (data as ApiError | null) ?? {
                message: `Error en la solicitud (status: ${response.status})`,
            };
            console.error('❌ Server Error:', error);
            throw {
                ...error,
                status: response.status,
            } satisfies ApiError;
        }

        return hasResponseBody ? data as T : undefined as T;
    }

    private async handleErrorResponse(response: Response): Promise<ApiError> {
        try {
            const error = await response.json() as ApiError;

            if (typeof error.message === 'string' && error.message.trim() !== '') {
                return error;
            }
        } catch {
            // fallback handled below
        }

        return {
            message: `Error en la solicitud (status: ${response.status})`,
            status: response.status,
        };
    }

    async get<T>(endpoint: string, includeAuth: boolean = true): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET' }, includeAuth);
    }

    async getQuiet<T>(endpoint: string, includeAuth: boolean = true): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET' }, includeAuth, true);
    }

    async post<T>(
        endpoint: string,
        body: unknown,
        includeAuth: boolean = true
    ): Promise<T> {
        const requestBody = this.prepareBody(body);

        return this.request<T>(
            endpoint,
            {
                method: 'POST',
                body: requestBody,
            },
            includeAuth
        );
    }

    async put<T>(
        endpoint: string,
        body: unknown,
        includeAuth: boolean = true
    ): Promise<T> {
        const requestBody = this.prepareBody(body);

        return this.request<T>(
            endpoint,
            {
                method: 'PUT',
                body: requestBody,
            },
            includeAuth
        );
    }

    async patch<T>(
        endpoint: string,
        body: unknown,
        includeAuth: boolean = true
    ): Promise<T> {
        const requestBody = this.prepareBody(body);

        return this.request<T>(
            endpoint,
            {
                method: 'PATCH',
                body: requestBody,
            },
            includeAuth
        );
    }

    async postForm<T>(endpoint: string, formData: FormData, includeAuth: boolean = true): Promise<T> {
        return this.request<T>(
            endpoint,
            {
                method: 'POST',
                body: formData,
            },
            includeAuth
        );
    }

    async postFormWithProgress<T>(
        endpoint: string,
        formData: FormData,
        onProgress?: (progressPercent: number) => void,
        includeAuth: boolean = true,
    ): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`;
        const deviceHeaders = await this.getDeviceHeaders('POST', endpoint);
        const headers = {
            ...this.getHeaders(includeAuth, formData),
            ...deviceHeaders,
        };

        return new Promise<T>((resolve, reject) => {
            const request = new XMLHttpRequest();

            request.open('POST', url);

            Object.entries(headers).forEach(([key, value]) => {
                if (typeof value === 'string') {
                    request.setRequestHeader(key, value);
                }
            });

            request.upload.onprogress = (event) => {
                if (!onProgress || !event.lengthComputable || event.total <= 0) {
                    return;
                }

                onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
            };

            request.onerror = () => {
                reject(new Error('No se pudo completar la carga del archivo.'));
            };

            request.onload = () => {
                const responseText = request.responseText?.trim();

                let data: unknown = null;

                if (responseText) {
                    try {
                        data = JSON.parse(responseText);
                    } catch {
                        reject(new Error(`Error parseando respuesta del servidor (status: ${request.status})`));
                        return;
                    }
                }

                if (request.status >= 200 && request.status < 300) {
                    resolve(data as T);
                    return;
                }

                reject(
                    (data as ApiError | null) ?? {
                        message: `Error en la solicitud (status: ${request.status})`,
                    },
                );
            };

            request.send(formData);
        });
    }

    async getBlob(endpoint: string, includeAuth: boolean = true): Promise<{ blob: Blob; headers: Headers }> {
        return this.requestBlob(endpoint, { method: 'GET' }, includeAuth);
    }

    async delete<T>(endpoint: string, includeAuth: boolean = true): Promise<T> {
        return this.request<T>(
            endpoint,
            {
                method: 'DELETE',
            },
            includeAuth
        );
    }
}

// Exportar instancia única del cliente
export const apiClient = new ApiClient(API_BASE_URL);
